import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {BulkAssign, BulkAssignActionOption} from './bulk-assign/BulkAssign';
import './BulkAssignFlyout.scss';
import {useGetBulkTagSelectedDocCount} from '../../../hooks/assignments/useAssignments';

// Types Definitions
// ----------------------------------
export interface BulkAssignContinuePayload {
  action: 'assign_documents' | 'unassign_documents';
  isNewAssignment: boolean;
  samplingMethod?: string;
  persistContent?: boolean;
  count?: number;
  percent?: number;
  docCount?: number;
}

interface BulkAssignFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: (payload: BulkAssignContinuePayload) => void;
  selectedIds?: string[];
  masterDocListId: number;
  onTreeSelect?: (ids: string[] | null) => void;
  initialSelectedAction?: BulkAssignActionOption;
}
// ------------------

const enBulkAssign = 'actionMenu.bulkAssignFlyout';
// ----------------------------------
// QuickBatchFlyout Component
// ----------------------------------
const BulkAssignFlyout: React.FC<BulkAssignFlyoutProps> = ({
  isOpen = false,
  onClose,
  selectedIds = [],
  onContinue,
  masterDocListId,
  onTreeSelect,
  initialSelectedAction = BulkAssignActionOption.AssignDoc,
}) => {
  const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(true);
  const [startingCount, setStartingCount] = useState(0);
  const [isStartingCountLoading, setIsStartingCountLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState<
    'assign_documents' | 'unassign_documents'
  >(initialSelectedAction ?? 'assign_documents');
  const [isNewAssignment, setIsNewAssignment] = useState<boolean>(false);
  const [samplingMethod, setSamplingMethod] = useState<string>('count');
  const [persistContent, setPersistContent] = useState<boolean>(false);
  const [count, setCount] = useState<number>(startingCount);
  const [percent, setPercent] = useState<number | undefined>(undefined);
  const startingCountInitializedRef = useRef<boolean>(false);
  // ------------------

  const handleBulkAssignError = (errorMessage: string | null) => {
    setIsSubmitDisable(Boolean(errorMessage));
  };
  // ------------------
  const shouldFetchCount = masterDocListId > 0;
  const {data} = useGetBulkTagSelectedDocCount(
    {masterDocListId},
    shouldFetchCount
  );

  const handleContinue = () => {
    const payload = {
      action: selectedAction,
      isNewAssignment,
      samplingMethod,
      persistContent,
      count,
      percent,
      docCount: startingCount,
    };
    if (selectedAction === 'unassign_documents') {
      onClose();
    }
    if (onContinue) onContinue(payload);
  };

  const handleOptionChange = useCallback(
    (values: {
      samplingMethod: string;
      persistContent: boolean;
      count: number;
      percent: number | undefined;
    }) => {
      setSamplingMethod(values.samplingMethod);
      setPersistContent(values.persistContent);
      setCount(values.count);
      setPercent(values.percent);
    },
    []
  );

  useEffect(() => {
    if (
      !startingCountInitializedRef.current &&
      data?.docCount !== undefined &&
      data.docCount !== -1
    ) {
      setStartingCount(data.docCount);
      setIsStartingCountLoading(false);
      startingCountInitializedRef.current = true;
    }
  }, [data?.docCount]);

  useEffect(() => {
    if (!isOpen) {
      startingCountInitializedRef.current = false;
    }
  }, [isOpen]);

  // ------------------

  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title=""
      side="right"
      size="large"
      showButton={false}
      showHeader={false}
      className="bulk-assign-flyout"
      ariaLabel="Quick Batch Flyout"
    >
      <Box className="bulk-assign-flyout__header">
        <Box className="title-box">
          <Typography
            className=" typo-text headline3_size title"
            data-testid="flyout-title"
            tabIndex={0}
          >
            {t(`${enBulkAssign}.flyoutTitle`)}
          </Typography>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              onClose();
            }}
            disabled={false}
            aria-label={t('common.cancel')}
            dataTestId="bulk-assign-flyout-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            className={isSubmitDisable ? 'btn-disabled' : ''}
            isAriaDisabled={isSubmitDisable}
            variant="primary"
            size="medium"
            type="submit"
            aria-label={t('common.continue')}
            dataTestId="bulk-assign-flyout-continue-button"
            onClick={handleContinue}
            tabIndex={0}
          >
            {t('common.continue')}
          </ArrButton>
        </Box>
      </Box>
      {/* Flyout Content */}
      <Box className="bulk-assign-flyout__content">
        <BulkAssign
          selectedIds={selectedIds}
          onError={handleBulkAssignError}
          setSelectedAction={setSelectedAction}
          onTabChange={(newTab) => setIsNewAssignment(newTab === 1)}
          startingCount={startingCount}
          isStartingCountLoading={isStartingCountLoading}
          onOptionChange={handleOptionChange}
          onTreeSelect={(ids) => {
            onTreeSelect?.(ids);
          }}
          initialSelectedAction={
            initialSelectedAction ?? BulkAssignActionOption.AssignDoc
          }
        />
      </Box>
    </ArrDrawer>
  );
};

export {BulkAssignFlyout};
export default BulkAssignFlyout;
